// Admin — Guides MILES : liste, performance par article, import Search Console, export snapshot
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Download, Plus, Upload } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";
import { PageHeader, Panel, T, Table, btnPrimary, btnSecondary, inputCls, MobileCards } from "@/components/admin/ui";
import { CLUSTERS } from "@/lib/guides";

const STATUS = { draft: ["Brouillon", "bg-amber-50 text-amber-700 border-amber-200"], published: ["Publié", "bg-emerald-50 text-emerald-700 border-emerald-200"] };
const ACTION_CLS = { maintenir: "text-slate", améliorer: "text-miles", actualiser: "text-amber-700", "améliorer title/meta": "text-miles" };

export default function AdminGuides() {
  const [perf, setPerf] = useState(null);
  const [filters, setFilters] = useState({ lang: "", status: "", category: "" });
  const [gsc, setGsc] = useState({ start: "", end: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [reload, setReload] = useState(0);

  useEffect(() => { adminApi.get("/guides/performance/all").then((r) => setPerf(r.data)).catch(() => {}); }, [reload]);

  const exportSnapshot = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/guides/export");
      toast.success(r.data.exported ? `${r.data.count} guide(s) exporté(s) — visibles en preview, redéployez pour la prod` : r.data.reason);
    } catch (e) { toast.error(apiError(e, "Export impossible")); } finally { setBusy(false); }
  };

  const importGsc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!gsc.start || !gsc.end) { toast.error("Indiquez la période du rapport (début / fin)"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("period_start", gsc.start); fd.append("period_end", gsc.end);
    setBusy(true);
    try {
      const r = await adminApi.post("/guides/gsc-import", fd);
      toast.success(`${r.data.imported} lignes Search Console importées (${gsc.start} → ${gsc.end})`);
      setReload((n) => n + 1);
    } catch (err) { toast.error(apiError(err, "Import impossible")); } finally { setBusy(false); e.target.value = ""; }
  };

  const rows = (perf?.guides || []).filter((g) => (!filters.lang || g.lang === filters.lang) && (!filters.status || g.status === filters.status) && (!filters.category || g.category === filters.category));
  const published = (perf?.guides || []).filter((g) => g.status === "published").length;
  const toReview = (perf?.guides || []).filter((g) => g.flags.length).length;

  return (
    <div className="space-y-6" data-testid="admin-guides">
      <PageHeader title="Guides MILES" testId="guides-header"
        subtitle={perf ? `${perf.guides.length} guides · ${published} publiés · ${toReview} à surveiller — publication en prod au prochain déploiement` : "Chargement…"}
        actions={<>
          <button type="button" onClick={exportSnapshot} disabled={busy} className={btnSecondary} data-testid="guides-export-button"><Download className="h-4 w-4" /> Exporter le snapshot</button>
          <Link to="/admin/app/guides/new" className={btnPrimary} data-testid="guides-new-button"><Plus className="h-4 w-4" /> Nouveau guide</Link>
        </>} />

      {perf?.cannibalization?.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="guides-cannibalization">
          <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Intentions trop proches entre guides publiés</p>
          <ul className="mt-2 space-y-1">{perf.cannibalization.map((c, i) => <li key={i}>[{c.lang}] « {c.a} » ↔ « {c.b} » — fusionner, rediriger (301) ou différencier l'intention</li>)}</ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
        <Panel title="Articles & performance" testId="guides-table-panel" padded={false}
          action={<div className="flex flex-wrap gap-2">
            <select value={filters.lang} onChange={(e) => setFilters({ ...filters, lang: e.target.value })} className={`${T.select} !h-9 !w-auto text-xs`} data-testid="guides-filter-lang"><option value="">Langue</option><option value="fr">FR</option><option value="en">EN</option><option value="de">DE</option></select>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={`${T.select} !h-9 !w-auto text-xs`} data-testid="guides-filter-status"><option value="">Statut</option><option value="draft">Brouillon</option><option value="published">Publié</option></select>
            <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className={`${T.select} !h-9 !w-auto text-xs`} data-testid="guides-filter-category"><option value="">Univers</option>{Object.keys(CLUSTERS).map((k) => <option key={k} value={k}>{CLUSTERS[k].name.fr}</option>)}</select>
          </div>}>
          <div className="px-5 pb-2 pt-3 text-xs text-slate">
            {perf?.gsc_period ? `Search Console : ${perf.gsc_period.period_start} → ${perf.gsc_period.period_end}${perf.gsc_previous ? ` (comparé à ${perf.gsc_previous.period_start} → ${perf.gsc_previous.period_end})` : ""}` : "Aucun import Search Console pour l'instant — les colonnes GSC resteront vides."}
          </div>
          <div className="hidden md:block">
          <Table head={["Guide", "Statut", "Vues", "CTA", "Résa", "Impr.", "Clics", "CTR", "Pos.", "Action"]} testId="guides-table">
            {rows.map((g) => (
              <tr key={g.id} className="hover:bg-cloud/60" data-testid={`guide-row-${g.slug}`}>
                <td>
                  <Link to={`/admin/app/guides/${g.id}`} className="font-medium text-ink hover:text-miles" data-testid={`guide-edit-${g.slug}`}>{g.title}</Link>
                  <p className={T.meta}>{g.lang.toUpperCase()} · {CLUSTERS[g.category]?.name.fr} · {g.path}</p>
                  {g.flags.length > 0 && <p className="mt-1 flex flex-wrap gap-1">{g.flags.map((f) => <span key={f} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700">{f}</span>)}</p>}
                </td>
                <td><span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS[g.status][1]}`}>{STATUS[g.status][0]}</span></td>
                <td className="font-mono text-xs">{g.internal.views}</td>
                <td className="font-mono text-xs">{g.internal.cta_clicks}</td>
                <td className="font-mono text-xs">{g.internal.booking_started}/{g.internal.bookings}</td>
                <td className="font-mono text-xs">{g.gsc ? g.gsc.impressions : "—"}</td>
                <td className="font-mono text-xs">{g.gsc ? g.gsc.clicks : "—"}</td>
                <td className="font-mono text-xs">{g.gsc ? `${g.gsc.ctr}%` : "—"}</td>
                <td className="font-mono text-xs">{g.gsc?.position ? g.gsc.position.toFixed(1) : "—"}</td>
                <td className={`text-xs font-semibold ${ACTION_CLS[g.action] || ""}`}>{g.action}</td>
              </tr>
            ))}
            {perf && !rows.length && <tr><td colSpan={10} className="py-10 text-center text-sm text-slate">Aucun guide pour ce filtre.</td></tr>}
          </Table>
          </div>
          <MobileCards testId="guides-cards-mobile">
            {perf && !rows.length && <p className="py-8 text-center text-sm text-slate">Aucun guide pour ce filtre.</p>}
            {rows.map((g) => (
              <Link key={g.id} to={`/admin/app/guides/${g.id}`} data-testid={`guide-card-${g.slug}`}
                className="block rounded-2xl border border-ink/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-transform active:scale-[0.99]">
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 font-medium text-ink">{g.title}</p>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS[g.status][1]}`}>{STATUS[g.status][0]}</span>
                </div>
                <p className={`${T.meta} mt-1`}>{g.lang.toUpperCase()} · {CLUSTERS[g.category]?.name.fr}</p>
                {g.flags.length > 0 && <p className="mt-2 flex flex-wrap gap-1">{g.flags.map((f) => <span key={f} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700">{f}</span>)}</p>}
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  {[["Vues", g.internal.views], ["CTA", g.internal.cta_clicks], ["Résa", `${g.internal.booking_started}/${g.internal.bookings}`],
                    ["Impr.", g.gsc ? g.gsc.impressions : "—"], ["Clics", g.gsc ? g.gsc.clicks : "—"], ["Pos.", g.gsc?.position ? g.gsc.position.toFixed(1) : "—"]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-ink/[0.03] py-1.5">
                      <p className="font-mono text-sm font-semibold text-ink">{v}</p>
                      <p className="text-[10px] uppercase tracking-wide text-slate/70">{l}</p>
                    </div>
                  ))}
                </div>
                <p className={`mt-2 text-xs font-semibold ${ACTION_CLS[g.action] || "text-slate"}`}>Action : {g.action}</p>
              </Link>
            ))}
          </MobileCards>
        </Panel>

        <div className="space-y-4">
          <Panel title="Import Search Console" testId="gsc-import-panel">
            <p className="text-xs leading-relaxed text-slate">Search Console → Performances → onglet <b>Pages</b> → Exporter (CSV). Colonnes attendues : Page, Clics, Impressions, CTR, Position. Importez une période à la fois ; le dernier import est comparé au précédent.</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <label className="text-xs text-slate">Début<input type="date" value={gsc.start} onChange={(e) => setGsc({ ...gsc, start: e.target.value })} className={`${inputCls} mt-1 !h-10`} data-testid="gsc-start" /></label>
              <label className="text-xs text-slate">Fin<input type="date" value={gsc.end} onChange={(e) => setGsc({ ...gsc, end: e.target.value })} className={`${inputCls} mt-1 !h-10`} data-testid="gsc-end" /></label>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importGsc} className="hidden" data-testid="gsc-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={`${btnSecondary} mt-3 w-full`} data-testid="gsc-import-button"><Upload className="h-4 w-4" /> Choisir le CSV et importer</button>
          </Panel>
          <Panel title="Lecture des signaux" testId="guides-legend">
            <ul className="space-y-1.5 text-xs text-slate">
              <li><b className="text-ink">croissance</b> — impressions +20 % vs période précédente</li>
              <li><b className="text-ink">stagnant</b> — impressions stables (±10 %)</li>
              <li><b className="text-ink">perte d'impressions / de clics</b> — −20 % → actualiser</li>
              <li><b className="text-ink">impressions élevées, CTR faible</b> — retravailler title / meta</li>
              <li><b className="text-ink">proche de la 1re page</b> — position 8–20 → améliorer le contenu</li>
              <li><b className="text-ink">ancien</b> — dernière révision &gt; 12 mois</li>
            </ul>
            <p className="mt-3 text-xs text-slate">Décision par article : maintenir · améliorer · actualiser · fusionner · rediriger · désindexer (noindex dans la fiche).</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}
